import React, { useState, useEffect } from 'react';
import { useFetch} from "../../../components/common/FetchProvider";
import {
  financialExpenseColumns, financialIncomeColumns, financialTransferColumns
} from "./variables/columnsData";
import ColumnsTable from "./components/ColumnsTable";
import Loader from "components/Loader/Loader";
import ExpenseModal from "./components/ExpenseModal";
import IncomeModal from "./components/IncomeModal";
import TransferModal from "./components/TransferModal";
import CurrencyModal from "./components/CurrencyModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";

import { FaEye, FaEyeSlash } from "react-icons/fa";
import ConfirmIncomeModal from "./components/ConfirmIncomeModal";
import ConfirmExpenseModal from "./components/ConfirmExpenseModal";

import {formatNumberToInternational, getMonthNumber, isSameMonthAndYear} from "../../../utils/utils";

const Tables = () => {
  const fetchWrapper = useFetch();
  const [organizationId, setOrganizationId] = useState("");
  const [organizationData, setOrganizationData] = useState([]);
  const [month, setMonth] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accountData, setAccountData] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [apiData, setApiData] = useState([]);
  const [existingFormData, setExistingFormData] = useState({});
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAmount, setShowAmount] = useState(true);
  const baseYear = 2024; // Set your base year here
  const currentYear = new Date().getFullYear(); // Get the current year
  const [yearOption, setYearOption] = useState(currentYear.toString()); // Default to current year
  const [years, setYears] = useState([]);
  const [selectedType, setSelectedType] = useState("despesa"); // Default to "Despesa"
  const [incomeConfirmModalState, setIncomeConfirmModalState] = useState(false);
  const [expenseConfirmModalState, setExpenseConfirmModalState] = useState(false);
  const [addPreviousMonthRecord, setAddPreviousMonthRecord] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [allowDelete, setAllowDelete] = useState(false);

  const allDropdownsSelected = organizationId && month && accountId;

  const handleYearChange = (e) => {
    setYearOption(e.target.value);
    handleOrganizationsChange(e);
  };

  const handleOrganizationsChange = (event) => {
    const selectedOrganizationId = event.target.value; // Get the selected value (id)
    setOrganizationId(selectedOrganizationId); // Update the state with the selected id
    // setMovement("");
    setMonth("");
    setAccountId("");
    setAvailableAccounts([]);
    const filteredAccounts = accountData.filter(
        (account) => account.organizationId === Number(selectedOrganizationId)
    );
    setAvailableAccounts(filteredAccounts);
  };

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
    setAccountId("");
    let selectedMonthYear = yearOption + '-'+getMonthNumber(e.target.value);
    setAllowDelete(isSameMonthAndYear(selectedMonthYear))
  };

  const handleAccountChange = (e) => {
    setApiData([]);
    setAccountId(e.target.value);
    fetchData(e.target.value, selectedType);
  }

  const getColumnsData = () => {
    switch (selectedType) {
      case "despesa":
        return financialExpenseColumns;
      case "recita":
        return financialIncomeColumns;
      case "transfers":
        return financialTransferColumns;
      default:
        return [];
    }
  };

  let mappedApiData = apiData.map((item) => ({
    id: item.id,
    seqNumber: item.seqNumber,
    beneficiary: item.beneficiaryName || item.beneficiary,
    description: item.description,
    paymentDate: item.paymentDate,
    sigfe: item.sigfe ? "Sim" : "Não",
    amount: formatNumberToInternational(item.amount),
    receivedDate: item.receivedDate,
    sourceDepartment: item.sourceDepartmentName,
    sourceAccount: item.sourceAccountName,
    destinationDepartment: item.destinationDepartmentName,
    destinationAccount: item.destinationAccountName,
    registrationDate: item.registrationDate,
    action: "View",
    report: "Download"
  }));

  useEffect(() => {
    fetchDropdownValues();
    // Generate the year options dynamically
    const yearOptions = [];
    for (let year = baseYear; year <= currentYear; year++) {
      yearOptions.push(year);
    }
    setYears(yearOptions);
  }, [baseYear, currentYear]);

  const refreshData = () => {
    setApiData([]);
    fetchData(accountId, selectedType);
  }

  const handleDropdownChange = (event) => {
    setApiData([]);
    setSelectedType(event.target.value);
    fetchData(accountId, event.target.value);
  };


  const [showModal, setShowModal] = useState(false);
  const handleLinkClick = () => {
    setShowModal(true);
  };
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const handleIncomeModalClick = () => {
    setShowIncomeModal(true);
  };

  const [showTransferModal, setShowTransferModal] = useState(false);
  const handleTransferModalClick = () => {
    setShowTransferModal(true);
  };

  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const handleCurrencyModalClick = () => {
    setShowCurrencyModal(true);
  };

  const handleEyeClick = () => {
    setShowAmount((prevShowEye) => !prevShowEye);
  };

  const fetchDropdownValues = async () => {
    setLoading(true);
    try {
      let apiUrl = `${process.env.REACT_APP_API_URL}/v1/financial-management/home/dropdown`;
      const userToken = localStorage.getItem("token");
      const response = await fetchWrapper(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      setLoading(false);
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.data) {
          setOrganizationData(responseData.data.organizations);
          setAccountData(responseData.data.accounts);
          setAddPreviousMonthRecord(responseData.data.allowPreviousMonthEdit);
        } else {
          setError(true);
        }
      } else {
        setError(true);
        console.error('Error fetching API data:', response.status);
      }
    } catch (error) {
      setLoading(false);
      setError(true);
      console.error('Error fetching API data:', error);
    }
  };

  const fetchData = async (selectedAccountId, dataType) => {
    setLoading(true);
    try {
      let apiUrl = `${process.env.REACT_APP_API_URL}/v1/financial-management/${dataType}?organizationId=${organizationId}&accountId=${selectedAccountId}&yearMonth=${yearOption}-${getMonthNumber(month)}`;

      const userToken = localStorage.getItem("token");
      const response = await fetchWrapper(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      setLoading(false);
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.data && responseData.data.length > 0) {
          setApiData(responseData.data);
        } else {
          setError(true);
        }
      } else {
        setError(true);
        console.error('Error fetching API data:', response.status);
      }
    } catch (error) {
      setLoading(false);
      setError(true);
      console.error('Error fetching API data:', error);
    }
  };

  const handleDelete = (rowData) => {
    let selectedData = apiData.find((item) => item.id === rowData.id);
    setExistingFormData(selectedData);
    setShowDeleteModal(true);
  }

  const handleViewClick = (rowData) => {
    let selectedData = apiData.find((item) => item.id === rowData.id);
    setExistingFormData(selectedData);
    switch (selectedType) {
      case "despesa":
        handleLinkClick();
        return;
      case "recita":
        handleIncomeModalClick();
        return;
      case "transfers":
        handleTransferModalClick();
        return;
      default:
        return;
    }
  };

  const viewReport = (rowData) => {
    let selectedData = apiData.find((item) => item.id === rowData.id);
    setExistingFormData(selectedData);
    switch (selectedType) {
      case "despesa":
        setExpenseConfirmModalState(true);
        return;
      case "recita":
        setIncomeConfirmModalState(true);
        return;
        case "transfers":
          window.open(`../reports/transfer-report?id=${rowData.id}`)
          return;
      default:
        return;
    }
      
  }

  return (
    <div className="10">
      <Loader show={loading} />

      <div className="mt-6 grid h-full grid-cols-1 gap-5 md:grid-cols-4">
        <div className="relative flex-1">
          <select
              value={yearOption}
              onChange={handleYearChange}
              className="w-full p-2.5 text-gray-800 bg-white/0 border rounded-lg shadow-sm outline-none dark:!border-white/10 dark:text-white"
          >
            <option value="">Select a year</option>
            {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
            ))}
          </select>
        </div>

        {/* First Dropdown */}
        <div className="relative flex-1">
          <select
              id="organizationId"
              value={organizationId}
              onChange={handleOrganizationsChange}
              className="w-full p-2.5 text-gray-800 bg-white/0 border rounded-lg shadow-sm outline-none dark:!border-white/10 dark:text-white"
          >
            <option value="">Selecione o órgão</option>
            {organizationData && organizationData.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
            ))}
          </select>
        </div>


        {/* Third Dropdown */}
        <div className="relative flex-1">
          <select
              value={month}
              onChange={handleMonthChange}
              className="w-full p-2.5 text-gray-800 bg-white/0 border rounded-lg shadow-sm outline-none dark:!border-white/10 dark:text-white"
          >
            <option value="">Selecione o mês</option>
            <option value="January">Janeiro</option>
            <option value="February">Fevereiro</option>
            <option value="March">Marchar</option>
            <option value="April">Abril</option>
            <option value="May">Poderia</option>
            <option value="June">Junho</option>
            <option value="July">Julho</option>
            <option value="August">Agosto</option>
            <option value="September">September</option>
            <option value="October">Outubro</option>
            <option value="November">Novembro</option>
            <option value="December">Dezembro</option>
          </select>
        </div>

        {/* Fourth Dropdown */}
        <div className="relative flex-1">
          <select
              value={accountId}
              onChange={handleAccountChange}
              disabled={!month}
              className="w-full p-2.5 text-gray-800 bg-white/0 border rounded-lg shadow-sm outline-none dark:!border-white/10 dark:text-white"
          >
            <option value="">Todas as contas</option>

            {availableAccounts && availableAccounts.map((accounts) => (
                <option key={accounts.id} value={accounts.id}>
                  {accounts.accountNumber ? accounts.accountNumber : accounts.accountName} - {accounts.accountCurrency}
                </option>
            ))}
          </select>
        </div>
      </div>

      {allDropdownsSelected && (
          <div>
            <header className="w-full pb-2 p-4 h-full relative flex items-center justify-between">
              <div className="text-xl font-bold text-navy-700 dark:text-white">
                Gestão Financeira DADOS
              </div>
              {/* Links with Separator */}
              <div className="flex justify-center items-center font-bold text-brand-600">

                <button onClick={handleEyeClick}>
                  {showAmount ? <FaEye/> : <FaEyeSlash/>}
                </button>
                <span className="mx-2">|</span>
                <button onClick={handleLinkClick}>Despesa</button>
                {showModal && (
                    <ExpenseModal
                        showModal={showModal}
                        setModalState={setShowModal}
                        refreshData={refreshData}
                        organizationData={organizationData}
                        organizationId={organizationId}
                        accountId={accountId}
                        availableAccounts={availableAccounts}
                        existingFormData={existingFormData}
                        setExistingFormData={setExistingFormData}
                        setExpenseConfirmModalState={setExpenseConfirmModalState}
                        addPreviousMonthRecord={addPreviousMonthRecord}
                    />
                )}
                {expenseConfirmModalState && (
                    <ConfirmExpenseModal
                        expenseConfirmModalState={expenseConfirmModalState}
                        setExpenseConfirmModalState={setExpenseConfirmModalState}
                        setExistingFormData={setExistingFormData}
                        expenseId={existingFormData.id}
                    />
                )}

                <span className="mx-2">|</span>
                <button onClick={handleIncomeModalClick}>Recita</button>
                {showIncomeModal && (
                    <IncomeModal
                        showModal={showIncomeModal}
                        setModalState={setShowIncomeModal}
                        refreshData={refreshData}
                        organizationData={organizationData}
                        organizationId={organizationId}
                        accountId={accountId}
                        availableAccounts={availableAccounts}
                        existingFormData={existingFormData}
                        setExistingFormData={setExistingFormData}
                        setIncomeConfirmModalState={setIncomeConfirmModalState}
                        addPreviousMonthRecord={addPreviousMonthRecord}
                    />
                )}

                {incomeConfirmModalState && (
                    <ConfirmIncomeModal
                        incomeConfirmModalState={incomeConfirmModalState}
                        setIncomeConfirmModalState={setIncomeConfirmModalState}
                        setExistingFormData={setExistingFormData}
                        recitaId={existingFormData.id}
                    />
                )}

                <span className="mx-2">|</span>
                <button onClick={handleTransferModalClick}>Transferir</button>
                {showTransferModal && (
                    <TransferModal
                        showModal={showTransferModal}
                        setModalState={setShowTransferModal}
                        refreshData={refreshData}
                        organizationData={organizationData}
                        organizationId={organizationId}
                        accountId={accountId}
                        availableAccounts={availableAccounts}
                        allAccounts={accountData}
                        existingFormData={existingFormData}
                        setExistingFormData={setExistingFormData}
                        addPreviousMonthRecord={addPreviousMonthRecord}
                    />
                )}
                <span className="mx-2">|</span>
                <button onClick={handleCurrencyModalClick}>Câmbio</button>
                {showCurrencyModal && (
                    <CurrencyModal
                        showModal={showCurrencyModal}
                        setModalState={setShowCurrencyModal}
                        year={yearOption}
                        month={month}
                    />
                )}

                {showDeleteModal && (
                    <DeleteConfirmModal
                        showModal={showDeleteModal}
                        setModalState={setShowDeleteModal}
                        existingFormData={existingFormData}
                        setExistingFormData={setExistingFormData}
                        refreshData={refreshData}
                        type={selectedType}
                    />
                )}
              </div>
            </header>

            <div>
              {/* Dropdown for type selection */}
              <div className="mt-3 grid h-full grid-cols-1 gap-5 md:grid-cols-4">
                <div className="relative flex-1">
                  <select
                      id="selectedType"
                      value={selectedType}
                      onChange={handleDropdownChange}
                      className="w-full p-2.5 text-gray-800 bg-white/0 border rounded-lg shadow-sm outline-none dark:!border-white/10 dark:text-white"
                  >
                    <option value="despesa">Despesa</option>
                    <option value="recita">Recita</option>
                    <option value="transfers">Transferir</option>
                  </select>
                </div>
              </div>


              {/* Table display */}
              <div className="mt-1">
                {loading ? (
                    <p>Loading...</p>
                ) : mappedApiData && mappedApiData.length >0 ? (
                    <ColumnsTable
                        columnsData={getColumnsData()} // Pass the correct columns
                        tableData={mappedApiData} // Pass the mapped data
                        onActionClick={handleViewClick}
                        viewReport={viewReport}
                        handleDelete={handleDelete}
                        showDelete={allowDelete}
                    />
                ) : (
                    <p className="text-red-500 min-h-[1.30rem]">Não existem dados disponíveis para este tipo</p>
                )}
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default Tables;
